import { z } from 'zod';
import { TASK_EVIDENCE_MODELS, taskCompletionEvidenceSpecSchema } from './task-evidence';

/**
 * The vocabulary of Clara's action registry (CUR-1159). Every action the agent can take on a
 * claim is parsed at this boundary before anything runs; ids travel as decimal strings because
 * tool-call JSON cannot carry a bigint.
 */

export const CLARA_ACTION_KEYS = ['createTask', 'completeTask'] as const;
export type ClaraActionKey = (typeof CLARA_ACTION_KEYS)[number];

/**
 * How an action may run for an org: `autonomous` runs at once, `veto` runs after a grace period
 * unless a handler objects, `approval` waits for a handler, `never` is refused. Slice 1 executes
 * `autonomous` only; the other three are refused until the policy work lands.
 */
export const CLARA_ACTION_POLICIES = ['autonomous', 'veto', 'approval', 'never'] as const;
export type ClaraActionPolicy = (typeof CLARA_ACTION_POLICIES)[number];

/** Largest value a Postgres int8 column holds; 19 digits admit values above it. */
const INT8_MAX = 9_223_372_036_854_775_807n;

/** Decimal-string bigint; a bounded character class, so the regex is linear, then bounded to int8. */
export const bigintStringSchema = z
    .string()
    .regex(/^\d{1,19}$/)
    // Zod runs the refinement even when the regex has already failed, so the conversion is
    // guarded: a malformed string is reported by the regex, never thrown by `BigInt`.
    .refine((value) => !/^\d{1,19}$/.test(value) || BigInt(value) <= INT8_MAX, {
        message: 'must fit a 64-bit integer',
    });

export const claraCreateTaskInputSchema = z
    .object({
        /** Stable key of the task on this claim (`Task.internalName`), e.g. `set_initial_reserve`. */
        key: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
        title: z.string().trim().min(1).max(200),
        description: z.string().trim().max(2000).optional(),
        /** ISO date-time; the task is due at the end of that day in the org's reporting timezone. */
        dueDate: z.string().datetime(),
        /** Org member to assign, or null for unassigned. */
        assigneeId: bigintStringSchema.nullable(),
        /** What completes the task. Null for a plain task (not used in slice 1). */
        completionEvidence: taskCompletionEvidenceSpecSchema.nullable(),
    })
    .strict();
export type ClaraCreateTaskInput = z.infer<typeof claraCreateTaskInputSchema>;

export const claraCompleteTaskInputSchema = z
    .object({
        taskId: bigintStringSchema,
        evidence: z.object({ model: z.enum(TASK_EVIDENCE_MODELS), id: bigintStringSchema }).strict(),
        note: z.string().trim().max(500).optional(),
    })
    .strict();
export type ClaraCompleteTaskInput = z.infer<typeof claraCompleteTaskInputSchema>;

/** The parsed input of any registered action, as stored on the idempotency ledger. */
export type ClaraActionInput = ClaraCreateTaskInput | ClaraCompleteTaskInput;
